import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FeatherClock, FeatherPlus, FeatherX, FeatherEdit2 } from '../ui/icons';
import { workPlanApi, taskApi } from '../../services/api';
import config from '../../config/setup';
import api from '../../services/api';
import './CloseWorkDay.css';
import { DefaultPageLayout } from '../layouts/DefaultPageLayout';
import PageHeader from '../Layout/PageHeader';
import { FaPaperclip, FaTrash, FaEdit } from 'react-icons/fa';
import TaskTypeIcon from '../shared/TaskTypeIcon';
import TaskDueDate from '../shared/TaskDueDate';
import { FeatureTag } from '../ui/feature-tag';
import { ProjectBadge } from '../ui/project-badge';
import WorkDayEvaluationModal from './WorkDayEvaluationModal';

const TASK_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed'
};

// Normalize status from database format to CloseWorkDay format
// Maps 'open' and 'released' to appropriate CloseWorkDay statuses
const normalizeStatus = (status) => {
  if (!status) return TASK_STATUS.IN_PROGRESS;
  
  const lowerStatus = status.toLowerCase();
  
  // Map database statuses to CloseWorkDay format
  switch (lowerStatus) {
    case 'open':
      return TASK_STATUS.OPEN; // 'open' maps to 'in-progress' in CloseWorkDay
    case 'in-progress':
      return TASK_STATUS.IN_PROGRESS;
    case 'completed':
      return TASK_STATUS.COMPLETED;
    case 'released':
      return TASK_STATUS.COMPLETED; // 'released' maps to 'completed' in CloseWorkDay
    default:
      return TASK_STATUS.IN_PROGRESS; // Default fallback
  }
};

const TaskForm = ({ task, onStatusUpdate, onHoursUpdate, onNotesUpdate, workPlanId, onAttachmentUpload, onAttachmentDelete, onTaskPullRequestUpdate }) => {
  // State management for individual task form
  // Each TaskForm component maintains its own local state for hours and notes
  // This prevents data loss when the parent component re-renders after attachment uploads
  const [isExpanded, setIsExpanded] = useState(true);
  const [localHours, setLocalHours] = useState(task.hours || '');
  const [localNotes, setLocalNotes] = useState(task.notes || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [taskPullRequests, setTaskPullRequests] = useState(task.task_pull_requests || []);
  const [showPullRequestDescription, setShowPullRequestDescription] = useState(false);
  const hasValidationErrors = !localHours || !localNotes.trim();

  // Initialize local state only once when component mounts
  useEffect(() => {
    if (!isInitialized) {
      setLocalHours(task.hours || '');
      setLocalNotes(task.notes || '');
      setTaskPullRequests(task.task_pull_requests || []);
      setIsInitialized(true);
    }
  }, [task.id]); // Only re-initialize if the task ID changes (different task)

  // Update local state immediately without server calls
  const handleLocalHoursChange = (value) => {
    setLocalHours(value);
    onHoursUpdate(task.id, value);
  };

  const handleLocalNotesChange = (value) => {
    setLocalNotes(value);
    onNotesUpdate(task.id, value);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadError('');
    
    try {
      // Use the correct task ID - prefer work_plan_task_id if available, otherwise use task.id
      const taskIdForApi = task.work_plan_task_id || task.workPlanTaskId || task.id;
      
      const updatedTask = await workPlanApi.uploadTaskAttachment(workPlanId, taskIdForApi, file);
      onAttachmentUpload(task.id, updatedTask);
      
      // Clear the file input
      event.target.value = '';
    } catch (err) {
      setUploadError('Failed to upload attachment.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      // Use the correct task ID - prefer work_plan_task_id if available, otherwise use task.id
      const taskIdForApi = task.work_plan_task_id || task.workPlanTaskId || task.id;
      
      const updatedTask = await workPlanApi.deleteWorkPlanAttachment(workPlanId, taskIdForApi, attachmentId);
      onAttachmentDelete(task.id, updatedTask);
    } catch (err) {
      console.error('Delete attachment error:', err);
      alert('Failed to delete attachment.');
    }
  };

  const addTaskPullRequest = () => {
    const newPullRequest = {
      id: Date.now(), // Simple ID for local state
      url: '',
      description: ''
    };
    const updatedPullRequests = [...taskPullRequests, newPullRequest];
    setTaskPullRequests(updatedPullRequests);
    onTaskPullRequestUpdate(task.id, updatedPullRequests);
  };

  const updateTaskPullRequest = (id, field, value) => {
    const updated = taskPullRequests.map(pr => 
      pr.id === id ? { ...pr, [field]: value } : pr
    );
    setTaskPullRequests(updated);
    onTaskPullRequestUpdate(task.id, updated);
  };

  const removeTaskPullRequest = (id) => {
    const updated = taskPullRequests.filter(pr => pr.id !== id);
    setTaskPullRequests(updated);
    onTaskPullRequestUpdate(task.id, updated);
  };

  const toggleTaskPullRequestDescription = () => {
    setShowPullRequestDescription(!showPullRequestDescription);
  };

  return (
    <div className="close-workday-task-card">
      <div 
        className="close-workday-task-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="close-workday-task-card-title">
          <div className="close-workday-task-title-content">
            <TaskTypeIcon taskType={task.task_type} className="close-workday-task-type-icon-wrapper" />
            <h5>{task.title}</h5>
            <ProjectBadge projectName={task.project?.name} />
            {task.feature_tag && (
              <FeatureTag 
                name={task.feature_tag.name} 
                color={task.feature_tag.color} 
                size="small" 
              />
            )}
            <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} className="close-workday-task-due-date" />
          </div>
          <div className="close-workday-task-header-actions">
            {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
              <span className="close-workday-attachment-count">
                <FaPaperclip /> {task.work_plan_attachments.length}
              </span>
            )}
            {taskPullRequests.length > 0 && (
              <span className="close-workday-pull-request-count">
                <FaEdit /> {taskPullRequests.length} PR{taskPullRequests.length !== 1 ? 's' : ''}
              </span>
            )}
            <button 
              className="close-workday-expand-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? '−' : '+'}
            </button>
          </div>
        </div>
      </div>
      
      <div className={`close-workday-task-card-content ${!isExpanded ? 'collapsed' : ''}`}>
        <div className="close-workday-task-form-fields">
          <div className="close-workday-form-row">
            <div className="close-workday-form-group">
              <label htmlFor={`status-${task.id}`}>Status:</label>
              <select
                id={`status-${task.id}`}
                value={task.status || TASK_STATUS.IN_PROGRESS}
                onChange={(e) => onStatusUpdate(task.id, e.target.value)}
                className="close-workday-status-select"
              >
                <option value={TASK_STATUS.OPEN}>Open</option>
                <option value={TASK_STATUS.IN_PROGRESS}>In-Progress</option>
                <option value={TASK_STATUS.COMPLETED}>Completed</option>
              </select>
            </div>

            <div className="close-workday-form-group">
              <label htmlFor={`hours-${task.id}`}>Hours Spent:</label>
              <input
                id={`hours-${task.id}`}
                type="number"
                value={localHours}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                  handleLocalHoursChange(value);
                }}
                min="0"
                step="0.5"
                className="close-workday-hours-input"
              />
            </div>
          </div>

          <div className="close-workday-form-group">
            <label htmlFor={`notes-${task.id}`}>Work Description:</label>
            <textarea
              id={`notes-${task.id}`}
              value={localNotes}
              onChange={(e) => handleLocalNotesChange(e.target.value)}
              placeholder="Describe the work done..."
              className="close-workday-notes-input"
            />
          </div>

          <div className="close-workday-form-group">
            <div className="close-workday-attachments-header">
              <label htmlFor={`attachments-${task.id}`}>Work Plan Attachments</label>
              <span className="close-workday-attachments-hint">

              <div className="close-workday-file-upload-wrapper">
              <div className="close-workday-file-upload-container">
                <input
                  type="file"
                  id={`attachments-${task.id}`}
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="close-workday-file-input"
                />
                <label htmlFor={`attachments-${task.id}`} className="close-workday-file-input-label">
                  <span className="close-workday-file-upload-icon">📎</span>
                  <span>{uploading ? 'Uploading...' : 'Add Files'}</span>
                </label>
              </div>
              {uploadError && <div className="close-workday-upload-error">{uploadError}</div>}
            </div>
              </span>
            </div>
            
            
            {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
              <div className="close-workday-work-plan-attachments-list">
                {task.work_plan_attachments.map(attachment => (
                  <div key={attachment.id} className="close-workday-work-plan-attachment-chip">
                    <a 
                      href={attachment.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="close-workday-work-plan-attachment-link"
                    >
                      <FaPaperclip className="close-workday-attachment-chip-icon" />
                      <span className="close-workday-attachment-chip-name">{attachment.file_name}</span>
                      <span className="close-workday-file-size">{(attachment.file_size / 1024).toFixed(1)}KB</span>
                    </a>
                    <button
                      className="close-workday-delete-attachment-btn"
                      title="Delete attachment"
                      onClick={() => handleDeleteAttachment(attachment.id)}
                    >
                      <FeatherX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Task Pull Requests Section */}
          <div className="close-workday-form-group">
            <label>Task Pull Requests:</label>
            <div className="close-workday-task-pull-requests-label">
              Add pull requests specifically related to this task's work.
            </div>
            
            {taskPullRequests.length === 0 ? (
              <div className="close-workday-no-task-pull-requests">
                <p>No pull requests added for this task.</p>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherPlus />}
                  onClick={addTaskPullRequest}
                >
                  Add Pull Request
                </Button>
              </div>
            ) : (
              <div className="close-workday-task-pull-requests-list">
                {taskPullRequests.map((pr, index) => (
                  <div key={pr.id} className="close-workday-task-pull-request-item">
                    <div className="close-workday-task-pull-request-header">
                      <span className="close-workday-task-pull-request-number">#{index + 1}</span>
                      <button
                        className="close-workday-remove-task-pull-request-btn"
                        onClick={() => removeTaskPullRequest(pr.id)}
                        title="Remove pull request"
                      >
                        <FeatherX size={10} />
                      </button>
                    </div>
                    
                    <div className="close-workday-form-group">
                      <label htmlFor={`task-pr-url-${task.id}-${pr.id}`}>Pull Request URL:</label>
                      <input
                        id={`task-pr-url-${task.id}-${pr.id}`}
                        type="url"
                        value={pr.url}
                        onChange={(e) => updateTaskPullRequest(pr.id, 'url', e.target.value)}
                        placeholder="https://github.com/owner/repo/pull/123"
                        className="close-workday-task-pull-request-url-input"
                      />
                    </div>

                    {showPullRequestDescription && (
                      <div className="close-workday-form-group">
                        <label htmlFor={`task-pr-description-${task.id}-${pr.id}`}>Description (Optional):</label>
                        <textarea
                          id={`task-pr-description-${task.id}-${pr.id}`}
                          value={pr.description}
                          onChange={(e) => updateTaskPullRequest(pr.id, 'description', e.target.value)}
                          placeholder="Brief description of changes..."
                          className="close-workday-task-pull-request-description-input"
                        />
                      </div>
                    )}
                  </div>
                ))}
                
                <div className="close-workday-task-pull-requests-actions">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    icon={<FeatherPlus />}
                    onClick={addTaskPullRequest}
                  >
                    Add Another PR
                  </Button>
                  
                  <Button
                    variant="neutral-tertiary"
                    size="small"
                    icon={<FeatherEdit2 />}
                    onClick={toggleTaskPullRequestDescription}
                  >
                    {showPullRequestDescription ? 'Hide' : 'Show'} Descriptions
                  </Button>
                </div>
              </div>
            )}
          </div>

          {hasValidationErrors && (
            <div className="close-workday-task-validation-message">
              <span className="close-workday-validation-title">Please complete all task details before submitting:</span>
              <ul>
                {!localHours && <li key="hours">Enter hours spent</li>}
                {!localNotes.trim() && <li key="notes">Add work description</li>}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WorkPlanSummary = ({ workPlan }) => {
  const formattedDate = new Date(workPlan.date).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const totalTasks = workPlan.tasks.length;
  const completedTasks = workPlan.tasks.filter(task => task.status === TASK_STATUS.COMPLETED).length;
  const inProgressTasks = workPlan.tasks.filter(task => task.status === TASK_STATUS.IN_PROGRESS).length;

  const totalHours = workPlan.tasks.reduce((sum, task) => sum + (task.hours || 0), 0);
  const totalPlannedHours = workPlan.tasks.reduce((sum, task) => sum + (task.plannedHours || 0), 0);

  return (
    <div className="close-workday-work-plan-summary">
      <div className="close-workday-summary-header">
        <h2>Work Plan for {formattedDate}</h2>
        <div className="close-workday-work-plan-stats">
          <div className="close-workday-stat-item">
            <span className="close-workday-stat-value">{totalTasks}</span>
            <span className="close-workday-stat-label">Total</span>
          </div>
          <div className="close-workday-stat-divider" />
          <div className="close-workday-stat-item">
            <span className="close-workday-stat-value">{completedTasks}</span>
            <span className="close-workday-stat-label">Completed</span>
          </div>
          <div className="close-workday-stat-divider" />
          <div className="close-workday-stat-item">
            <span className="close-workday-stat-value">{inProgressTasks}</span>
            <span className="close-workday-stat-label">In Progress</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PullRequestSection = ({ pullRequests, onPullRequestsChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  const addPullRequest = () => {
    const newPullRequest = {
      id: Date.now(), // Simple ID for local state
      url: '',
      description: ''
    };
    onPullRequestsChange([...pullRequests, newPullRequest]);
  };

  const updatePullRequest = (id, field, value) => {
    const updated = pullRequests.map(pr => 
      pr.id === id ? { ...pr, [field]: value } : pr
    );
    onPullRequestsChange(updated);
  };

  const removePullRequest = (id) => {
    const updated = pullRequests.filter(pr => pr.id !== id);
    onPullRequestsChange(updated);
  };

  const toggleDescription = () => {
    setShowDescription(!showDescription);
  };

  return (
    <div className="close-workday-pull-requests-section">
      <div 
        className="close-workday-pull-requests-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="close-workday-pull-requests-title">
          <h5>Pull Requests</h5>
          <div className="close-workday-pull-requests-header-actions">
            {pullRequests.length > 0 && (
              <span className="close-workday-pull-request-count">
                {pullRequests.length} PR{pullRequests.length !== 1 ? 's' : ''}
              </span>
            )}
            <button 
              className="close-workday-expand-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? '−' : '+'}
            </button>
          </div>
        </div>
      </div>
      
      <div className={`close-workday-pull-requests-content ${!isExpanded ? 'collapsed' : ''}`}>
        {pullRequests.length === 0 ? (
          <div className="close-workday-no-pull-requests">
            <p>No pull requests added yet.</p>
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherPlus />}
              onClick={addPullRequest}
            >
              Add Pull Request
            </Button>
          </div>
        ) : (
          <div className="close-workday-pull-requests-list">
            {pullRequests.map((pr, index) => (
              <div key={pr.id} className="close-workday-pull-request-item">
                <div className="close-workday-pull-request-header">
                  <span className="close-workday-pull-request-number">#{index + 1}</span>
                  <button
                    className="close-workday-remove-pull-request-btn"
                    onClick={() => removePullRequest(pr.id)}
                    title="Remove pull request"
                  >
                    <FeatherX size={16} />
                  </button>
                </div>
                
                <div className="close-workday-form-group">
                  <label htmlFor={`pr-url-${pr.id}`}>Pull Request URL:</label>
                  <input
                    id={`pr-url-${pr.id}`}
                    type="url"
                    value={pr.url}
                    onChange={(e) => updatePullRequest(pr.id, 'url', e.target.value)}
                    placeholder="https://github.com/owner/repo/pull/123"
                    className="close-workday-pull-request-url-input"
                  />
                </div>

                {showDescription && (
                  <div className="close-workday-form-group">
                    <label htmlFor={`pr-description-${pr.id}`}>Description (Optional):</label>
                    <textarea
                      id={`pr-description-${pr.id}`}
                      value={pr.description}
                      onChange={(e) => updatePullRequest(pr.id, 'description', e.target.value)}
                      placeholder="Brief description of changes..."
                      className="close-workday-pull-request-description-input"
                    />
                  </div>
                )}
              </div>
            ))}
            
            <div className="close-workday-pull-requests-actions">
              <Button
                variant="neutral-secondary"
                size="small"
                icon={<FeatherPlus />}
                onClick={addPullRequest}
              >
                Add Another PR
              </Button>
              
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherEdit2 />}
                onClick={toggleDescription}
              >
                {showDescription ? 'Hide' : 'Show'} Descriptions
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CloseWorkDay = () => {
  const navigate = useNavigate();
  const { workPlanId } = useParams();
  const { user } = useAuth();
  const [workPlan, setWorkPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pullRequests, setPullRequests] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [formTasksData, setFormTasksData] = useState(null);
  const [isCheckingVersion, setIsCheckingVersion] = useState(true);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const errorMessageRef = useRef(null);

  // Version check on component mount
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const clientVersion = config.app.version;
        
        // Set timeout for version check (5 seconds)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Version check timeout')), 5000)
        );
        
        const versionCheckPromise = api.get('/api/config/version');
        
        const serverResponse = await Promise.race([versionCheckPromise, timeoutPromise]);
        const serverVersion = serverResponse.data?.version;
        
        if (serverVersion && serverVersion !== clientVersion) {
          // Version mismatch - show modal with refresh option
          console.log(`Version mismatch detected. Client: ${clientVersion}, Server: ${serverVersion}`);
          setServerVersion(serverVersion);
          setShowVersionModal(true);
          return;
        }
      } catch (error) {
        // If version check fails (network error, timeout, etc.), allow user to proceed
        console.warn('Version check failed, allowing user to proceed:', error.message);
      } finally {
        setIsCheckingVersion(false);
      }
    };

    checkVersion();
  }, []);

  useEffect(() => {
    // Only load work plan after version check completes
    if (!isCheckingVersion) {
      loadWorkPlanAndTasks();
    }
  }, [workPlanId, isCheckingVersion]);

  const loadWorkPlanAndTasks = async () => {
    try {
      let activePlan;
      if (workPlanId) {
        // If workPlanId is provided, get that specific work plan
        activePlan = await workPlanApi.get(workPlanId);
      } else {
        // Otherwise get the active work plan
        activePlan = await workPlanApi.getActive();
      }

      if (!activePlan) {
        navigate('/dashboard');
        return;
      }

      const allTasks = await taskApi.getAll();

      // Combine work plan tasks with their full task data
      const workPlanWithTaskDetails = {
        ...activePlan,
        tasks: activePlan.tasks.map(planTask => {
          // Find the full task data
          const taskData = allTasks.find(t => t.id === (planTask.taskId || planTask.task_id));
          
          if (!taskData) return null;

          // Normalize the task data structure
          const normalizedTask = {
            ...taskData,
            ...planTask,
            // Preserve the original task ID for component identification
            id: taskData.id,
            // Store the work plan task ID for API calls
            work_plan_task_id: planTask.work_plan_task_id,
            workPlanTaskId: planTask.work_plan_task_id,
            status: normalizeStatus(planTask.status_task),
            hours: planTask.hours || 0,
            notes: planTask.notes || '',
            plannedHours: planTask.plannedHours || planTask.planned_hours || 0
          };

          return normalizedTask;
        }).filter(Boolean)
      };

      setWorkPlan(workPlanWithTaskDetails);
      setTasks(allTasks);
    } catch (error) {
      console.error('Failed to load work plan:', error);
      setError('Failed to load work plan data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = (taskId, newStatus) => {
    // Update only local state
    setWorkPlan(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    }));
  };

  const handleHoursUpdate = (taskId, hours) => {
    // Update only local state
    setWorkPlan(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, hours: Number(hours) } : task
      )
    }));
  };

  const handleNotesUpdate = (taskId, notes) => {
    // Update only local state
    setWorkPlan(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, notes } : task
      )
    }));
  };

  const handleAttachmentUpload = (taskId, updatedTask) => {
    // Update only local state with the entire updated task, but preserve user's current input
    setWorkPlan(prev => {
      const currentTask = prev.tasks.find(task => task.id === taskId);
      return {
        ...prev,
        tasks: prev.tasks.map(task =>
          task.id === taskId ? {
            ...currentTask, // Keep all current task data
            // Only update the work plan attachments from the API response
            work_plan_attachments: updatedTask.work_plan_attachments || currentTask.work_plan_attachments
          } : task
        )
      };
    });
  };

  const handleAttachmentDelete = (taskId, updatedTask) => {
    // Update only local state with the entire updated task, but preserve user's current input
    setWorkPlan(prev => {
      const currentTask = prev.tasks.find(task => task.id === taskId);
      return {
        ...prev,
        tasks: prev.tasks.map(task =>
          task.id === taskId ? {
            ...currentTask, // Keep all current task data
            // Only update the work plan attachments from the API response
            work_plan_attachments: updatedTask.work_plan_attachments || currentTask.work_plan_attachments
          } : task
        )
      };
    });
  };

  const handleTaskPullRequestUpdate = (taskId, pullRequests) => {
    // Update only local state for task pull requests
    setWorkPlan(prev => ({
      ...prev,
      tasks: prev.tasks.map(task =>
        task.id === taskId ? { ...task, task_pull_requests: pullRequests } : task
      )
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setIsEvaluating(true);
    try {
      // Validate all tasks have required fields
      const hasInvalidTasks = workPlan.tasks.some(task => 
        !task.hours || !task.notes?.trim()
      );

      if (hasInvalidTasks) {
        setError('Please complete all task details before closing the work plan');
        setIsSubmitting(false);
        setIsEvaluating(false);
        // Scroll to error message after state update
        setTimeout(() => {
          errorMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // First, evaluate the work plan with current form data
      try {
        // Prepare current form data (unsaved hours/status/notes) to include in evaluation
        const tasksData = workPlan.tasks.map(task => ({
          taskId: task.task_id || task.id,
          hours: task.hours,
          status: task.status,
          notes: task.notes
        }));
        
        const evaluationResult = await workPlanApi.evaluate(workPlan.id, { tasks: tasksData });
        
        // Backend handles idempotency - if evaluation already completed, returns hasIssues: false
        // If evaluation exists but not completed, returns hasIssues: true with existing data
        if (evaluationResult && evaluationResult.hasIssues) {
          // Show evaluation modal with form data for accurate hour calculations
          setEvaluation(evaluationResult);
          setFormTasksData(tasksData); // Store form data to pass to modal
          setShowEvaluationModal(true);
          setIsSubmitting(false);
          setIsEvaluating(false);
          return;
        }
      } catch (evalError) {
        console.error('Error evaluating work plan:', evalError);
        // Continue with closing even if evaluation fails
      }

      // If no issues or evaluation failed, proceed with closing
      await closeWorkPlan();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError('Failed to process work plan closure');
      setIsSubmitting(false);
      setIsEvaluating(false);
    }
  };

  const closeWorkPlan = async () => {
    try {
      // Prepare tasks data for API
      const tasksToSend = workPlan.tasks.map(task => ({
        workPlanTaskId: task.work_plan_task_id || task.workPlanTaskId,
        status: task.status,
        hours: task.hours,
        notes: task.notes,
        pullRequests: (task.task_pull_requests || [])
          .filter(pr => pr.url && pr.url.trim())
          .map(pr => ({
            url: pr.url.trim(),
            description: pr.description?.trim() || ''
          }))
      }));

      // Prepare pull requests data for API (filter out empty URLs)
      const pullRequestsToSend = pullRequests
        .filter(pr => pr.url && pr.url.trim())
        .map(pr => ({
          url: pr.url.trim(),
          description: pr.description?.trim() || ''
        }));

      // Close the work plan and update all tasks and pull requests in one go
      await workPlanApi.close(workPlan.id, {
        tasks: tasksToSend,
        pullRequests: pullRequestsToSend
      });

      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error closing work plan:', error);
      setError('Failed to close work plan');
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleEvaluationComplete = async () => {
    try {
      // Close modal
      setShowEvaluationModal(false);
      setEvaluation(null);
      
      // Proceed with closing work plan
      setIsSubmitting(true);
      await closeWorkPlan();
    } catch (error) {
      console.error('Error closing work plan:', error);
      setError('Failed to close work plan');
      setIsSubmitting(false);
    }
  };

  const handleCancelEvaluation = () => {
    setShowEvaluationModal(false);
    setEvaluation(null);
    setFormTasksData(null);
    setIsSubmitting(false);
    setIsEvaluating(false);
  };

  const handleRefresh = () => {
    // Force hard refresh to get latest version
    window.location.href = window.location.href;
  };

  // Show loading while checking version or loading work plan
  if (isCheckingVersion || loading) return (
    <DefaultPageLayout>
      <div className="close-workday-container">
        <PageHeader title="Close Work Day" showBackButton={true} />
        <div className="close-workday-loading-container">Loading...</div>
      </div>
    </DefaultPageLayout>
  );

  // Don't show form if there's an error and no work plan (initial load error)
  if (error && !workPlan) return (
    <DefaultPageLayout>
      <div className="close-workday-container">
        <PageHeader title="Close Work Day" showBackButton={true} />
        <div className="close-workday-error-message">{error}</div>
      </div>
    </DefaultPageLayout>
  );

  // Version update modal
  const VersionUpdateModal = () => {
    if (!showVersionModal) return null;

    return (
      <div 
        className="workday-evaluation-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
      >
        <div 
          style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: '600' }}>
            Update Available
          </h2>
          <p style={{ marginBottom: '24px', color: '#666', lineHeight: '1.5' }}>
            A new version of the application is available (v{serverVersion}). 
            Please refresh to get the latest features and improvements.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              onClick={handleRefresh}
            >
              Refresh Now
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <VersionUpdateModal />
      <DefaultPageLayout>
      <div className="close-workday-container">
        <PageHeader title="Close Work Day" showBackButton={false} />
        {workPlan && <WorkPlanSummary workPlan={workPlan} />}
        <div className="close-workday-tasks-container">
          {workPlan.tasks.map(task => (
            <TaskForm
              key={task.id}
              task={task}
              onStatusUpdate={handleStatusUpdate}
              onHoursUpdate={handleHoursUpdate}
              onNotesUpdate={handleNotesUpdate}
              workPlanId={workPlan.id}
              onAttachmentUpload={handleAttachmentUpload}
              onAttachmentDelete={handleAttachmentDelete}
              onTaskPullRequestUpdate={handleTaskPullRequestUpdate}
            />
          ))}
          
          <PullRequestSection 
            pullRequests={pullRequests}
            onPullRequestsChange={setPullRequests}
          />
          
          {error && (
            <div 
              ref={errorMessageRef}
              className="close-workday-error-message" 
              style={{ 
                marginBottom: '1rem', 
                padding: '0.75rem 1rem', 
                backgroundColor: '#fee2e2', 
                border: '1px solid #fecaca', 
                borderRadius: '0.5rem', 
                color: '#991b1b',
                fontWeight: '500'
              }}
            >
              {error}
            </div>
          )}
          
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isEvaluating}
            className="close-workday-submit-button"
          >
            {isEvaluating ? 'Evaluating...' : isSubmitting ? 'Closing Work Plan...' : 'Close Work Plan'}
          </Button>
        </div>
      </div>

      {showEvaluationModal && evaluation && (
        <WorkDayEvaluationModal
          evaluation={evaluation}
          workPlanId={workPlan.id}
          formTasksData={formTasksData}
          onComplete={handleEvaluationComplete}
          onCancel={handleCancelEvaluation}
        />
      )}
      </DefaultPageLayout>
    </>
  );
};

export default CloseWorkDay; 